import {OutgoingInvoiceProjectItem} from "./OutgoingInvoiceProjectItem";
import {Identifier, makeNumericId} from "../common/Identifier";
import {BasicBillableItem, BillableItems, ProjectPhaseTaskFlatRateBillable} from "./AccountBook";
import {ProjectStagePhaseTask} from "../projects/ProjectStagePhaseTask";
import {makeSensitive, Sensitive} from "../common/Sensitive";
import {InvoiceItemType} from "./InvoiceItemType";
import {OutgoingInvoiceItemId} from "./OutgoingInvoiceItem";
import {Money, Percent} from "./Finance";


/**
 * Represents an item in an outgoing invoice specifically linked to a project task.
 * Extends basic project item properties with task-specific flat rate billing details.
 */
export interface OutgoingInvoiceProjectTaskItem extends Omit< OutgoingInvoiceProjectItem, "id" >,
    ProjectPhaseTaskFlatRateBillable,
    OutgoingInvoiceItemId
{
    /** Unique identifier for the invoice project task item. */
    id: Identifier< number >
    /** Sensitive money value representing any additional costs incurred. */
    additionalCosts: Sensitive<Money>
    /** The percentage rate applied to additional costs. */
    additionalCostsPercent: Percent

}

/**
 * Type guard to check if a billable item is specifically a project task flat rate item.
 * @param obj The billable item to check.
 * @returns True if the item is an OutgoingInvoiceProjectTaskItem.
 */
export function isInvoiceProjectTaskFlatRateItem(obj: BasicBillableItem ): obj is OutgoingInvoiceProjectTaskItem {
    return obj.hasOwnProperty("phaseTaskId")
        && obj.hasOwnProperty("phasePerformanceInTime" )
        && obj.invoiceItemType == InvoiceItemType.FLAT_RATE
}

/**
 * Converts a ProjectStagePhaseTask from the project management domain into an OutgoingInvoiceProjectTaskItem
 * for accounting and invoicing purposes. Initializes default values for financial fields.
 * @param phaseTask The project task to be converted.
 * @returns A new OutgoingInvoiceProjectTaskItem initialized with task data.
 */
export function phaseTaskToInvoiceItem(
    phaseTask: ProjectStagePhaseTask
): OutgoingInvoiceProjectTaskItem {

    return {
        id: makeNumericId( 0 ),
        outgoingInvoiceItemId: makeNumericId( 0 ),
        phaseId: phaseTask.phaseIdPath.phaseId,
        phaseTaskId: phaseTask.projectStagePhaseTaskId,
        stageId: phaseTask.phaseIdPath.projectStageId,
        name: phaseTask.name,
        phasePerformanceInTime: phaseTask.performance,
        billedPerformance: 0.00,
        phaseBudgetInTime: phaseTask.retailPrice,
        lastBilledPerformance: 0.0,
        additionalCosts: makeSensitive( 0.0 ),
        additionalCostsPercent: 0.0,
        totals: {
            salesTax: { salesTax: 19.00, salesTaxAmount: makeSensitive( 0.00 ) },
            amountOwed: makeSensitive( 0.00 ),
            amountOwedWithTax: makeSensitive( 0.00 ),
        },
        isDeleted: false,
        invoiceId: makeNumericId( 0.00 ),
        invoiceItemType: phaseTask.projectBillType.toString().toLowerCase() === "single_invoice_time"
            ? InvoiceItemType.TIME
            : InvoiceItemType.FLAT_RATE
    };

}